import { FC } from "react";
import { Box, CircularProgress, IconButton, Link, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { PanelLayout } from "../PanelLayout";
import { useOnchainConsentInfo } from "../../hooks/useOnchainConsentInfo";

const explorers: Record<string, string> = {
  "84532": "https://base-sepolia.blockscout.com",
  "8453": "https://base.blockscout.com",
};

const addressPattern = /^0x[a-fA-F0-9]{40}$/;
const transactionHashPattern = /^0x[a-fA-F0-9]{64}$/;

const formatStatus = (status: string): string => {
  const normalized = status.toUpperCase();
  if (normalized === "PREPARED") return "Prepared";
  if (normalized === "SUBMITTED") return "Submitted";
  if (normalized === "CONFIRMED") return "Confirmed";
  if (normalized === "FAILED") return "Failed";
  return status.toLowerCase().replace(/(^|[_\s-])\w/g, (letter) => letter.toUpperCase());
};

const copyToClipboard = (value: string) => {
  navigator.clipboard.writeText(value);
};

const ConsentValue: FC<{
  label: string;
  value?: string;
  placeholder?: string;
  href?: string;
}> = ({ label, value, placeholder, href }) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {href && value ? (
        <Link
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontFamily: "monospace", fontSize: "0.875rem", wordBreak: "break-all" }}
        >
          {value}
        </Link>
      ) : (
        <Typography
          variant="body2"
          sx={{ flex: 1, fontFamily: "monospace", wordBreak: "break-all" }}
        >
          {value ?? placeholder}
        </Typography>
      )}
      {value && (
        <IconButton
          aria-label={`Copy ${label}`}
          size="small"
          onClick={() => copyToClipboard(value)}
          sx={{ flexShrink: 0, padding: 0.5 }}
        >
          <ContentCopyIcon sx={{ fontSize: "0.875rem" }} />
        </IconButton>
      )}
    </Box>
  </Box>
);

export const OnchainConsentPanel: FC<{ verificationRequestId?: string }> = ({
  verificationRequestId,
}) => {
  const [info, isLoading, error] = useOnchainConsentInfo(verificationRequestId);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography role="alert" color="error" variant="body2">
        Error loading onchain consent information: {error.message}
      </Typography>
    );
  }

  if (!info) {
    return null;
  }

  const explorer = info ? explorers[info.chainId] : undefined;
  const pending = info?.status === "PREPARED" || info?.status === "SUBMITTED";
  const missingValue = pending ? "Pending confirmation" : "Unavailable";
  const contractHref = explorer && addressPattern.test(info?.contractAddress ?? "")
    ? `${explorer}/address/${info?.contractAddress}`
    : undefined;
  const transactionHref = explorer && transactionHashPattern.test(info?.transactionHash ?? "")
    ? `${explorer}/tx/${info?.transactionHash}`
    : undefined;

  return (
    <PanelLayout title="Onchain consent">
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <ConsentValue label="Contract address" value={info.contractAddress} href={contractHref} />
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Consent status
            </Typography>
            <Typography variant="body2">{formatStatus(info.status)}</Typography>
          </Box>
          <ConsentValue label="Issued token ID" value={info.tokenId} placeholder={missingValue} />
          <ConsentValue
            label="Mint transaction hash"
            value={info.transactionHash}
            placeholder={missingValue}
            href={transactionHref}
          />
        </Box>
      </Box>
    </PanelLayout>
  );
};
